import './underwriting.overview.underwriting-attachments.scss';

import {
  Box,
  Dialog,
  DialogContent,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import {
  FileUploader,
  FormContainerWithHeader,
} from '../../../../presentation';

import { ApolloError } from '@apollo/client';
import { openInNewTab } from '@converge/utils';
import { useConvergeSnackBar } from '../../../../contexts';
import { useUnderwritingSubmissionStateContext } from '../../underwriting.submission.context';
import { useUpsertSubmissionMutation } from '@converge/graphql';
import { Dispatch, SetStateAction } from 'react';

export const UnderwritingOverviewUnderwritingAttachmentsFormSection = ({
  isClaimAttachment,
  setShowUploadDialog,
  showUploadDialog,
}: {
  isClaimAttachment?: boolean;
  showUploadDialog: boolean;
  setShowUploadDialog: Dispatch<SetStateAction<boolean>>;
}) => {
  const {
    submission: { attachments, uuid: submissionUuid },
  } = useUnderwritingSubmissionStateContext();

  // const { claimSubmissionDetails: claimDetails } =
  //   useOutletContext<IClaimProps>();

  // const { attachments: ClaimSubmissionAttachment, uuid: submissionUuid2 } = claimDetails;

  const { notify, closeSnackbar } = useConvergeSnackBar();

  const { upsertSubmission } = useUpsertSubmissionMutation();

  return (
    <FormContainerWithHeader title="Attachments">
      <List className="attachment-container">
        {attachments?.edges?.length === 0 && (
          <Box className="no-attachment-message">
            <Typography color="gray">No attachments</Typography>
          </Box>
        )}
        {attachments?.edges?.map((attachment, index) => {
          return (
            <ListItem
              disablePadding
              key={
                attachment.cursor ?? attachment.node.uuid ?? `index-${index}`
              }
            >
              <ListItemButton
                onClick={() => {
                  if (attachment.node.url) {
                    openInNewTab(attachment.node.url);
                  }
                }}
                className="list-item-button"
              >
                <ListItemText
                  primary={
                    <Typography component="span" fontSize="16px">
                      {attachment.node.folder} /{' '}
                      {attachment.node.filePath?.split('/').slice(-1)[0]}
                    </Typography>
                  }
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
      <Dialog
        maxWidth={false}
        open={showUploadDialog}
        onClose={() => setShowUploadDialog(false)}
      >
        <DialogContent sx={{ width: '50vw' }} className="file-upload-modal">
          <FileUploader
            label="Attachments"
            enableFolderSelection
            existingFiles={
              attachments?.edges?.map((edge) => ({
                name: edge.node.filePath?.split('/').slice(-1)[0] ?? '',
                folder: edge.node.folder ?? '',
              })) ?? []
            }
            onFileHandler={async (data) => {
              if (data.acceptedFiles.length === 0) {
                return;
              }

              const alert = notify(`Uploading file...`, {
                variant: 'info',
              });

              try {
                const result = await upsertSubmission({
                  variables: {
                    details: {
                      uuid: submissionUuid,
                      attachments: {
                        files: data.acceptedFiles.map((file) => {
                          return {
                            file: file.file,
                            folder: file.folder,
                          };
                        }),
                      },
                    },
                  },
                  context: {
                    fetchOptions: {
                      useUpload: true,
                      onProgress: (ev: ProgressEvent) => {
                        for (const file of data.acceptedFiles) {
                          data.onProgressChange(ev, file);
                        }
                      },
                    },
                  },
                  errorPolicy: 'all',
                });
                closeSnackbar(alert);
                const graphqlErrors = result?.errors;
                if (graphqlErrors?.length) {
                  data.onError(
                    new Error(graphqlErrors[0].message),
                    data.acceptedFiles[0]
                  );
                  notify(`Failed to upload file: ${graphqlErrors[0].message}`, {
                    variant: 'error',
                  });
                  return;
                }
                notify(`Successfully uploaded file.`, {
                  variant: 'success',
                });
              } catch (cause) {
                closeSnackbar(alert);

                data.onError(
                  cause instanceof Error || cause instanceof ApolloError
                    ? cause
                    : new Error('Unknown error'),
                  data.acceptedFiles[0]
                );
                notify(
                  `Failed to upload file: ${
                    cause instanceof Error ? cause.message : 'unknown error'
                  }`,
                  {
                    variant: 'error',
                  }
                );
              }
            }}
            hiddenLabel
          />
        </DialogContent>
      </Dialog>
    </FormContainerWithHeader>
  );
};
